import { useState, useEffect, useCallback } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  Settings,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Home,
} from 'lucide-react'
import { useAuth } from '../../core/auth'
import { useUnreadCounts } from '../contexts/UnreadCountsContext'
import { useActiveModule, MODULE_DEFINITIONS, type NavGroup } from '../contexts/ModuleContext'

// Map route paths to their lazy-import functions for prefetching
const prefetchMap: Record<string, () => Promise<unknown>> = {
  '/lng-ops': () => import('../../features/dashboard/Dashboard'),
  '/lng-ops/live-view': () => import('../../features/vessels/LiveView'),
  '/lng-ops/voyage-performance': () => import('../../features/voyage-performance/VoyagePerformance'),
  '/lng-ops/vessel-reports': () => import('../../features/vessel-reports/VesselReports'),
  '/lng-ops/graphs': () => import('../../features/graphs/Graphs'),
  '/lng-ops/reports': () => import('../../features/reports/Reports'),
  '/lng-ops/scheduler': () => import('../../features/scheduler/Scheduler'),
  '/lng-ops/notifications': () => import('../../features/messages/Messages'),
  '/lng-ops/admin': () => import('../../features/admin/Admin'),
}

// Track which routes have already been prefetched to avoid duplicate imports
const prefetched = new Set<string>()

function prefetchRoute(path: string) {
  if (prefetched.has(path)) return
  prefetched.add(path)
  const loader = prefetchMap[path]
  if (loader) loader()
}

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed'

export default function Sidebar() {
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const activeModule = useActiveModule()
  const { notifications: unreadCount, messages: msgUnread } = useUnreadCounts()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
      // Default to expanded on first visit (saved === null)
      return saved === null ? false : saved === 'true'
    } catch { return false }
  })

  // Get nav groups from active module definition
  const moduleDefinition = MODULE_DEFINITIONS.find((m) => m.id === activeModule)
  const navGroups: NavGroup[] = moduleDefinition?.navGroups ?? []

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)) } catch { /* */ }
      return next
    })
  }, [])

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Close mobile sidebar on Escape
  useEffect(() => {
    if (!mobileOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [mobileOpen])

  const handlePrefetch = useCallback((path: string) => {
    prefetchRoute(path)
  }, [])

  function getBadge(to: string): number {
    if (to === '/lng-ops/scheduler') return unreadCount
    if (to === '/lng-ops/notifications') return msgUnread
    return 0
  }

  // Determine if a NavLink should use "end" matching (only for module index routes)
  function shouldEnd(to: string): boolean {
    return to === '/lng-ops' || to === '/fsru'
  }

  // Atlas LNG is single-product — no module selector, no home button.
  const showHomeButton = false

  const sidebarContent = (isExpanded: boolean) => (
    <>
      {/* Back to Home + Module Title */}
      <div className={`flex items-center gap-2.5 px-3 mb-2 h-10 flex-shrink-0 ${isExpanded ? '' : 'justify-center'}`}>
        {showHomeButton && (
          <button
            onClick={() => navigate('/home')}
            className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-text-muted hover:text-accent transition-colors"
            title="Back to Home"
          >
            <Home className="h-4 w-4" strokeWidth={1.8} />
          </button>
        )}
        {isExpanded && (
          <span className="text-sm font-semibold text-text-primary tracking-wide whitespace-nowrap">
            {moduleDefinition?.label ?? 'LNG OPS'}
          </span>
        )}
      </div>

      {/* Nav Groups */}
      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden px-2">
        {navGroups.map((group, gi) => (
          <div key={gi} className={gi > 0 ? 'mt-3' : ''}>
            {isExpanded && group.title && (
              <div className="px-2 mb-1.5">
                <span className="text-[10px] font-semibold text-text-muted/50">{group.title}</span>
              </div>
            )}
            {!isExpanded && gi > 0 && group.title && (
              <div className="mx-auto my-1.5 h-px w-5 bg-border" />
            )}
            {group.items.map((item) => {
              // Notifications tab is admin-only — historical messages reference
              // real vessel names which bypass the alias layer. Show the nav
              // link only to admins.
              if (item.to === '/lng-ops/notifications' && user?.role !== 'admin') {
                return null
              }
              const badge = getBadge(item.to)
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-tour={item.to}
                  end={shouldEnd(item.to)}
                  onMouseEnter={() => handlePrefetch(item.to)}
                  className={({ isActive }) =>
                    `group relative flex items-center rounded-lg transition-colors ${
                      isExpanded ? 'h-8 gap-2.5 px-2.5' : 'h-9 w-9 mx-auto justify-center'
                    } ${
                      isActive
                        ? 'bg-accent-muted text-accent'
                        : 'text-text-muted hover:bg-bg-hover hover:text-text-secondary'
                    }`
                  }
                >
                  <item.icon className="h-[16px] w-[16px] flex-shrink-0" strokeWidth={1.8} />
                  {isExpanded && (
                    <span className="text-xs font-medium truncate">{item.label}</span>
                  )}
                  {badge > 0 && (
                    <span className={`${isExpanded ? 'ml-auto' : 'absolute -top-0.5 -right-0.5'} h-3.5 min-w-3.5 rounded-full bg-accent flex items-center justify-center text-[8px] font-bold text-accent-fg px-0.5`}>
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                  {/* Tooltip — only when collapsed */}
                  {!isExpanded && (
                    <div className="pointer-events-none absolute left-full ml-3 z-[9999] whitespace-nowrap rounded-md bg-bg-tertiary px-2.5 py-1.5 text-xs font-medium text-text-primary opacity-0 shadow-lg transition-opacity duration-150 delay-0 group-hover:opacity-100 group-hover:delay-300">
                      {item.label}
                    </div>
                  )}
                </NavLink>
              )
            })}
          </div>
        ))}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Admin - only for admins in lng-ops module */}
        {user?.role === 'admin' && activeModule === 'lng-ops' && (
          <NavLink
            to="/lng-ops/admin"
            onMouseEnter={() => handlePrefetch('/lng-ops/admin')}
            className={({ isActive }) =>
              `group relative flex items-center rounded-lg transition-colors mb-1 ${
                isExpanded ? 'h-8 gap-2.5 px-2.5' : 'h-9 w-9 mx-auto justify-center'
              } ${
                isActive
                  ? 'bg-accent-muted text-accent'
                  : 'text-text-muted hover:bg-bg-hover hover:text-text-secondary'
              }`
            }
          >
            <Settings className="h-[16px] w-[16px] flex-shrink-0" strokeWidth={1.8} />
            {isExpanded && <span className="text-xs font-medium">Admin</span>}
            {!isExpanded && (
              <div className="pointer-events-none absolute left-full ml-3 z-[9999] whitespace-nowrap rounded-md bg-bg-tertiary px-2.5 py-1.5 text-xs font-medium text-text-primary opacity-0 shadow-lg transition-opacity duration-150 delay-0 group-hover:opacity-100 group-hover:delay-300">
                Admin
              </div>
            )}
          </NavLink>
        )}

        {/* Collapse Toggle */}
        <button
          onClick={toggleCollapsed}
          className={`flex items-center rounded-lg transition-colors text-text-muted hover:bg-bg-hover hover:text-text-secondary ${
            isExpanded ? 'h-8 gap-2.5 px-2.5' : 'h-9 w-9 mx-auto justify-center'
          }`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-[16px] w-[16px]" strokeWidth={1.8} /> : <ChevronLeft className="h-[16px] w-[16px]" strokeWidth={1.8} />}
          {isExpanded && <span className="text-xs font-medium">Collapse</span>}
        </button>
      </nav>
    </>
  )

  return (
    <>
      {/* Mobile hamburger button — visible below lg */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-3 left-3 z-50 flex h-9 w-9 items-center justify-center rounded-lg bg-bg-secondary text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors lg:hidden"
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Desktop sidebar — hidden below lg */}
      <aside
        data-tour="sidebar"
        className={`hidden lg:flex h-full flex-col bg-bg-secondary py-3 transition-[width] duration-200 ease-in-out ${
          collapsed ? 'w-[52px]' : 'w-[180px]'
        }`}
      >
        {sidebarContent(!collapsed)}
      </aside>

      {/* Mobile sidebar overlay — visible below lg when open */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
          />
          {/* Sidebar panel — always expanded on mobile */}
          <aside className="relative flex h-full w-[200px] flex-col bg-bg-secondary py-3 shadow-ambient animate-slide-in-left">
            {/* Close button */}
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute -right-10 top-3 flex h-8 w-8 items-center justify-center rounded-lg bg-bg-secondary text-text-muted hover:text-text-primary transition-colors"
              aria-label="Close navigation"
            >
              <X className="h-4 w-4" />
            </button>
            {sidebarContent(true)}
          </aside>
        </div>
      )}
    </>
  )
}
